import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Save, Loader2, Sparkles, Scale, Hourglass, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIP_LABELS = {
  asansor: 'Asansör', jenerator: 'Jeneratör', ups: 'UPS', trafo: 'Trafo', yangin_algilama: 'Yangın Algılama', sprinkler: 'Sprinkler',
  duman_tahliye: 'Duman Tahliye', hvac_klima: 'HVAC / Klima', buhar_kazani: 'Buhar Kazanı', lpg_tanki: 'LPG Tankı', forklift: 'Forklift',
  yapi_iskelesi: 'Yapı İskelesi', yuruyen_merdiven: 'Yürüyen Merdiven', topraklama: 'Topraklama', paratoner: 'Paratoner', katodik_koruma: 'Katodik Koruma',
  su_deposu: 'Su Deposu', kompanzasyon: 'Kompanzasyon', boyler: 'Boyler', genlesme_tanki: 'Genleşme Tankı', kazan: 'Kazan', brulor: 'Brülör',
  kum_filtresi: 'Kum Filtresi', transpalet: 'Transpalet', erisim_platformu: 'Erişim Platformu', diger: 'Diğer',
};
const AGIRLIK_LABELS = { A: 'A · Arıza olasılığı', M: 'M · Mevzuat (gecikme)', Y: 'Y · Yaşlanma', E: 'E · Kapanmamış eksiklik' };

/** PK Ayarlar > Yapay Zekâ sekmesi — Ekipman Zekâsı parametreleri (anahtar girişi YOK: herkes kendi anahtarını Profil'den girer) */
export default function YapayZekaTab({ isAdmin }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai/settings`, { withCredentials: true });
      setS(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Yapay zekâ ayarları yüklenemedi'); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) return <Card className="bg-[#1A2332] border-gray-800"><CardContent className="p-6 text-sm text-gray-400">Ekipman Zekâsı parametrelerini yalnızca admin düzenleyebilir. Yapay zekâ API anahtarınızı Profil &gt; Yapay Zekâ bölümünden girebilirsiniz.</CardContent></Card>;
  if (!s) return <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…</div>;

  const agirlikToplam = Object.values(s.risk_weights || {}).reduce((a, b) => a + Number(b || 0), 0);

  const saveParams = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/ai/settings`, {
        monthly_limit: Number(s.monthly_limit), llm_clamp_pts: Number(s.llm_clamp_pts),
        economic_life: s.economic_life, safety_factors: s.safety_factors, risk_weights: s.risk_weights, cost_bands_tl: s.cost_bands_tl,
      }, { withCredentials: true });
      setS(res.data);
      toast.success('Ekipman Zekâsı parametreleri kaydedildi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const setNested = (key, tip, val) => setS(prev => ({ ...prev, [key]: { ...prev[key], [tip]: val } }));

  return (
    <div className="space-y-4" data-testid="pk-yapay-zeka-tab">
      <Card className="bg-[#1A2332] border-gray-800">
        <CardContent className="p-5 space-y-2">
          <h3 className="text-white font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#E67E22]" /> API Anahtarları</h3>
          <p className="text-xs text-gray-300" data-testid="ai-anahtar-bilgi">Organizasyon anahtarı <b>yoktur</b>. Her kullanıcı (yönetici dahil) yapay zekâ özelliklerini <b>yalnızca kendi</b> API anahtarıyla kullanır; anahtar ve model tercihi <b>Profil › Yapay Zekâ</b> bölümünden girilir.</p>
          <p className="text-[11px] text-gray-500">Otomatik/zamanlanmış filo taraması kapalıdır; tarama yalnızca Ekipman Zekâsı › Filo Taraması'nda "Şimdi Tara" ile, tıklayan yöneticinin kendi anahtarıyla çalışır. Arıza/kontrol girişinde otomatik analiz yapılmaz; analizler manuel "Analiz Et" ile başlatılır.</p>
        </CardContent>
      </Card>

      <Card className="bg-[#1A2332] border-gray-800">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E67E22]" /> Kullanım Politikası</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-gray-300">Aylık analiz limiti (filo taraması için)</Label>
              <Input type="number" value={s.monthly_limit ?? ''} onChange={e => setS({ ...s, monthly_limit: e.target.value })} className="bg-gray-800 border-gray-700 text-white h-8" data-testid="ai-monthly-limit" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-300">LLM skor düzeltme sınırı (± puan)</Label>
              <Input type="number" value={s.llm_clamp_pts ?? 15} onChange={e => setS({ ...s, llm_clamp_pts: e.target.value })} className="bg-gray-800 border-gray-700 text-white h-8" data-testid="ai-clamp" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#1A2332] border-gray-800">
        <CardContent className="p-5 space-y-3">
          <h3 className="text-white font-semibold flex items-center gap-2"><Scale className="w-4 h-4 text-[#E67E22]" /> Risk Ağırlıkları <span className={`text-xs font-normal ${Math.abs(agirlikToplam - 1) > 0.01 ? 'text-red-400' : 'text-emerald-400'}`}>toplam {agirlikToplam.toFixed(2)} (1.00 olmalı)</span></h3>
          <p className="text-[11px] text-gray-500">TabanRisk = 100 × G × (A·wA + M·wM + Y·wY + E·wE)</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(AGIRLIK_LABELS).map(([k, l]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs text-gray-300">{l}</Label>
                <Input type="number" step="0.05" min="0" max="1" value={s.risk_weights?.[k] ?? ''} onChange={e => setNested('risk_weights', k, e.target.value)} className="bg-gray-800 border-gray-700 text-white h-8" data-testid={`ai-weight-${k}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#1A2332] border-gray-800">
        <CardContent className="p-5 space-y-3">
          <h3 className="text-white font-semibold flex items-center gap-2"><Scale className="w-4 h-4 text-[#E67E22]" /> Maliyet Bandı Orta Noktaları (₺ / ekipman / yıl)</h3>
          <p className="text-[11px] text-gray-500">Maliyet Öngörü Raporu, uzman analizlerindeki düşük/orta/yüksek bantlarını bu tutarlarla TL'ye çevirir.</p>
          <div className="grid grid-cols-3 gap-3">
            {[['dusuk', 'Düşük bant'], ['orta', 'Orta bant'], ['yuksek', 'Yüksek bant']].map(([k, l]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs text-gray-300">{l}</Label>
                <Input type="number" min="0" step="500" value={s.cost_bands_tl?.[k] ?? ''} onChange={e => setNested('cost_bands_tl', k, e.target.value)} className="bg-gray-800 border-gray-700 text-white h-8" data-testid={`ai-cost-${k}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#1A2332] border-gray-800">
        <CardContent className="p-5 space-y-3">
          <h3 className="text-white font-semibold flex items-center gap-2"><Hourglass className="w-4 h-4 text-[#E67E22]" /> Ekonomik Ömür (yıl) ve Güvenlik Katsayısı (G)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.keys(TIP_LABELS).map(tip => (
              <div key={tip} className="flex items-center gap-2 bg-gray-900/60 border border-gray-800 rounded px-2 py-1.5">
                <span className="text-xs text-gray-200 flex-1 truncate">{TIP_LABELS[tip]}</span>
                <Input type="number" min="1" value={s.economic_life?.[tip] ?? ''} onChange={e => setNested('economic_life', tip, e.target.value)} title="Ekonomik ömür (yıl)" className="bg-gray-800 border-gray-700 text-white h-7 w-16 text-xs" data-testid={`ai-life-${tip}`} />
                <span className="text-[10px] text-gray-500">yıl</span>
                <Input type="number" step="0.05" min="0" max="1" value={s.safety_factors?.[tip] ?? ''} onChange={e => setNested('safety_factors', tip, e.target.value)} title="Güvenlik katsayısı G" className="bg-gray-800 border-gray-700 text-white h-7 w-16 text-xs" data-testid={`ai-g-${tip}`} />
                <span className="text-[10px] text-gray-500">G</span>
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={saveParams} disabled={saving || Math.abs(agirlikToplam - 1) > 0.01} className="bg-[#E67E22] hover:bg-[#d3701c] text-white h-9" data-testid="ai-params-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Parametreleri Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
